/**
 * Pepnow "Spin to win" popup.
 *
 * Markup + settings come from snippets/pepnow-spin-wheel.liquid. Flow:
 *   wait until the page has fully loaded and been *visible* for N seconds
 *   -> spin (always lands on a non-minimum tier)
 *   -> collect first/last name + email via the Shopify customer form
 *   -> reveal the tier's code (and apply it to the cart session).
 *
 * Add ?spin=test to any URL to open it immediately, ignoring the saved
 * "already seen" state (works in "Test link only" and "On" modes).
 */
(() => {
  const root = document.querySelector('[data-pn-spin]');
  const configEl = document.querySelector('[data-pn-spin-config]');
  if (!root || !configEl || root.dataset.pnReady === 'true') return;
  root.dataset.pnReady = 'true';

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (error) {
    return;
  }

  const DEFAULT_TEXT = {
    wonHeading: 'You won {discount} off!',
    wonSub: "Enter your details to claim it. We'll show your code right away.",
    claimButton: 'Claim my {discount} off',
    doneHeading: 'Your {discount} off code',
    doneSub: "It's applied at checkout automatically, or copy it below.",
  };
  const text = { ...DEFAULT_TEXT };
  Object.entries(config.text || {}).forEach(([key, value]) => {
    if (typeof value === 'string' && value.trim()) text[key] = value;
  });
  config.text = text;

  const STORAGE_KEY = 'pepnow_spin_v1';
  const PROMO_STORAGE_KEY = 'pepnow_popup_30off';
  const DAY_MS = 24 * 60 * 60 * 1000;
  const SLOT_TIERS = [0, 1, 2, 3, 0, 1, 2, 3];
  const SLOT_COLORS = [
    { fill: '#262720', text: '#fffffb' },
    { fill: '#cbbdb0', text: '#262720' },
    { fill: '#857162', text: '#fffffb' },
    { fill: '#efe8df', text: '#262720' },
  ];

  const params = new URLSearchParams(window.location.search);
  const isTestLink = params.get('spin') === 'test';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const tiers = (config.tiers || []).map((tier, index) => {
    const code = String(tier.code || '').trim();
    const weight = Math.max(0, Number(tier.weight) || 0);
    // Tier 0 is the "minimum" shown on the wheel but never awarded.
    return { label: String(tier.label || ''), code, weight: index === 0 || !code ? 0 : weight };
  });
  const winnable = tiers.map((tier, index) => (tier.code && index > 0 ? index : -1)).filter((i) => i > 0);
  if (tiers.length < 2 || winnable.length === 0) return;

  const el = (selector) => root.querySelector(selector);
  const titleEl = el('[data-pn-spin-title]');
  const subEl = el('[data-pn-spin-sub]');
  const wheelEl = el('[data-pn-spin-wheel]');
  const liveEl = el('[data-pn-spin-live]');
  const spinBtn = el('[data-pn-spin-go]');
  const form = el('.pn-spin__form');
  const tagsInput = el('[data-pn-spin-tags]');
  const submitBtn = el('[data-pn-spin-submit]');
  const errorEl = el('[data-pn-spin-error]');
  const codeEl = el('[data-pn-spin-code]');
  const copyBtn = el('[data-pn-spin-copy]');
  const shopLink = el('[data-pn-spin-shop]');
  const skipBtn = el('[data-pn-spin-skip]');

  /* ---------- persisted state ---------- */

  const readState = () => {
    if (isTestLink) return {};
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch (error) {
      return {};
    }
  };

  const writeState = (patch) => {
    if (isTestLink) return;
    try {
      const next = { ...readState(), ...patch, ts: Date.now() };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (error) {
      /* storage unavailable — non-fatal */
    }
  };

  let wonTier = null;

  const fill = (template, tier) => String(template || '').replace(/\{discount\}/g, tier.label);

  /* ---------- eligibility ---------- */

  // The targeted 30%-off campaign popup takes priority over the wheel.
  const promoCampaignActive = () => {
    const promo = config.promo || {};
    if (promo.enabled && promo.param && params.has(promo.param)) {
      if (!promo.value) return true;
      return String(params.get(promo.param)).toLowerCase() === String(promo.value).toLowerCase();
    }
    return false;
  };

  const isEligible = () => {
    if (isTestLink) return true;
    if (config.mode !== 'on' || config.designMode) return false;
    if (promoCampaignActive()) return false;
    try {
      if (localStorage.getItem(PROMO_STORAGE_KEY) === 'claimed') return false;
    } catch (error) {
      /* ignore */
    }
    const state = readState();
    if (state.status === 'claimed') return false;
    if (state.status === 'dismissed') {
      const days = Math.max(0, Number(config.dismissDays) || 0);
      if (Date.now() - (state.ts || 0) < days * DAY_MS) return false;
    }
    return true;
  };

  /* ---------- wheel drawing ---------- */

  const point = (angleDeg, radius) => {
    const a = (angleDeg * Math.PI) / 180;
    return [100 + radius * Math.sin(a), 100 - radius * Math.cos(a)];
  };

  const escapeXml = (value) =>
    String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const drawWheel = () => {
    const slotAngle = 360 / SLOT_TIERS.length;
    const radius = 94;
    let segments = '';
    let pegs = '';

    SLOT_TIERS.forEach((tierIndex, slot) => {
      const start = slot * slotAngle;
      const end = start + slotAngle;
      const [x0, y0] = point(start, radius);
      const [x1, y1] = point(end, radius);
      const color = SLOT_COLORS[slot % SLOT_COLORS.length];
      const [labelX, labelY] = [100, 100 - radius * 0.66];
      const [pegX, pegY] = point(start, radius - 3);

      segments +=
        `<path d="M100 100 L${x0.toFixed(2)} ${y0.toFixed(2)} A${radius} ${radius} 0 0 1 ${x1.toFixed(2)} ${y1.toFixed(2)} Z" fill="${color.fill}"/>` +
        `<text x="${labelX}" y="${labelY.toFixed(2)}" transform="rotate(${start + slotAngle / 2} 100 100)" fill="${color.text}" ` +
        `font-size="17" font-weight="700" text-anchor="middle" dominant-baseline="middle">${escapeXml(tiers[tierIndex].label)}</text>`;
      pegs += `<circle cx="${pegX.toFixed(2)}" cy="${pegY.toFixed(2)}" r="2.4" fill="#fffffb"/>`;
    });

    wheelEl.innerHTML =
      `<svg viewBox="0 0 200 200" role="img" aria-hidden="true" focusable="false">` +
      `<circle cx="100" cy="100" r="99" fill="#262720"/>` +
      segments +
      pegs +
      `<circle cx="100" cy="100" r="17" fill="#fffffb" stroke="#262720" stroke-width="4"/>` +
      `<circle cx="100" cy="100" r="5" fill="#8a5045"/>` +
      `</svg>`;
  };

  /* ---------- steps ---------- */

  const showStep = (name) => {
    root.querySelectorAll('[data-pn-step]').forEach((step) => {
      step.hidden = step.dataset.pnStep !== name;
    });
    root.dataset.pnStep = name;
    if (skipBtn) skipBtn.hidden = name === 'done';
  };

  const showClaim = (tierIndex) => {
    const tier = tiers[tierIndex];
    wonTier = tierIndex;
    titleEl.textContent = fill(config.text.wonHeading, tier);
    subEl.textContent = config.text.wonSub || '';
    submitBtn.textContent = fill(config.text.claimButton, tier);
    liveEl.textContent = `You won ${tier.label} off.`;
    showStep('claim');
  };

  const showDone = (tierIndex) => {
    const tier = tiers[tierIndex];
    wonTier = tierIndex;
    titleEl.textContent = fill(config.text.doneHeading, tier);
    subEl.textContent = config.text.doneSub || '';
    codeEl.textContent = tier.code;
    const redirect = encodeURIComponent(config.shopUrl || '/collections/all');
    shopLink.href = `/discount/${encodeURIComponent(tier.code)}?redirect=${redirect}`;
    liveEl.textContent = `Your code is ${tier.code}.`;
    showStep('done');

    // Best-effort: attach the code to this visitor's cart session so it
    // applies at checkout without typing it in.
    fetch(`/discount/${encodeURIComponent(tier.code)}?redirect=%2Fcart.js`, { credentials: 'same-origin' }).catch(
      () => {}
    );
  };

  // Only move focus into text fields on devices with a mouse, so phones
  // don't pop the keyboard over the wheel unprompted.
  const finePointer = window.matchMedia('(pointer: fine)').matches;
  const focusStep = (step) => {
    if (step === 'spin') spinBtn.focus();
    else if (step === 'done') copyBtn?.focus();
    else if (step === 'claim' && finePointer) form?.querySelector('input[name="contact[first_name]"]')?.focus();
  };

  /* ---------- spin ---------- */

  const slotAngle = 360 / SLOT_TIERS.length;

  const restingRotation = (tierIndex) => {
    const slot = SLOT_TIERS.indexOf(tierIndex);
    return 360 - (slot * slotAngle + slotAngle / 2);
  };

  const pickTier = () => {
    const total = winnable.reduce((sum, index) => sum + tiers[index].weight, 0);
    if (total <= 0) return winnable[0];
    let roll = Math.random() * total;
    for (const index of winnable) {
      roll -= tiers[index].weight;
      if (roll < 0) return index;
    }
    return winnable[winnable.length - 1];
  };

  const spin = () => {
    spinBtn.disabled = true;
    spinBtn.textContent = 'Spinning…';

    const tierIndex = pickTier();
    const slots = SLOT_TIERS.map((t, slot) => (t === tierIndex ? slot : -1)).filter((s) => s >= 0);
    const slot = slots[Math.floor(Math.random() * slots.length)];
    // Land somewhere inside the slot, never right on a divider.
    const target = slot * slotAngle + slotAngle / 2 + (Math.random() - 0.5) * slotAngle * 0.6;
    const turns = reduceMotion ? 1 : 5;
    const duration = reduceMotion ? 900 : 3800;

    wheelEl.style.transition = `transform ${duration}ms cubic-bezier(0.12, 0.67, 0.16, 1)`;
    wheelEl.style.transform = `rotate(${turns * 360 + (360 - target)}deg)`;

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      writeState({ status: 'spun', won: tierIndex });
      window.setTimeout(() => {
        showClaim(tierIndex);
        focusStep('claim');
      }, 350);
    };
    wheelEl.addEventListener('transitionend', finish, { once: true });
    window.setTimeout(finish, duration + 400);
  };

  /* ---------- claim ---------- */

  const slug = (value) => String(value).toLowerCase().replace(/[^a-z0-9]+/g, '');

  form?.addEventListener('submit', (event) => {
    event.preventDefault();
    if (wonTier === null) return;
    if (errorEl) errorEl.hidden = true;

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const tier = tiers[wonTier];
    if (tagsInput) tagsInput.value = `newsletter, spin-to-win, spin-${slug(tier.label) || wonTier}`;

    submitBtn.disabled = true;
    const label = submitBtn.textContent;
    submitBtn.textContent = 'Submitting…';

    fetch(form.action, { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } })
      .then((response) => {
        // Shopify's spam protection can bounce form posts to a challenge page.
        // Fall back to a normal submit so the visitor can complete it; the
        // popup re-opens on the confirmed page with their code.
        if (response.redirected && /\/challenge/.test(response.url)) {
          writeState({ status: 'pending', won: wonTier });
          HTMLFormElement.prototype.submit.call(form);
          return;
        }
        writeState({ status: 'claimed', won: wonTier });
        showDone(wonTier);
        focusStep('done');
      })
      .catch(() => {
        submitBtn.disabled = false;
        submitBtn.textContent = label;
        if (errorEl) {
          errorEl.textContent = 'Something went wrong. Please try again.';
          errorEl.hidden = false;
        }
      });
  });

  copyBtn?.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(codeEl.textContent);
      copyBtn.textContent = 'Copied!';
      window.setTimeout(() => {
        copyBtn.textContent = 'Copy';
      }, 1800);
    } catch (error) {
      /* clipboard blocked — the code is still visible */
    }
  });

  /* ---------- open / close ---------- */

  const lockScroll = (locked) => {
    document.documentElement.classList.toggle('pn-spin-open', locked);
  };

  const close = () => {
    if (root.open) root.close();
  };

  root.addEventListener('close', () => {
    lockScroll(false);
    if (readState().status !== 'claimed') writeState({ status: 'dismissed', won: wonTier });
  });

  root.querySelectorAll('[data-pn-spin-close]').forEach((button) => button.addEventListener('click', close));

  // Clicks on the dimmed backdrop land on the <dialog> element itself.
  root.addEventListener('click', (event) => {
    if (event.target === root) close();
  });

  let cssReady = null;
  const loadCss = () => {
    if (!cssReady) {
      cssReady = new Promise((resolve) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = config.cssUrl;
        link.onload = () => resolve(true);
        link.onerror = () => resolve(false);
        document.head.appendChild(link);
      });
    }
    return cssReady;
  };

  const open = (startStep) => {
    if (root.open) return;
    drawWheel();
    if (startStep === 'spin') {
      showStep('spin');
    } else {
      // Returning visitor: show the wheel resting on the prize they already won.
      wheelEl.style.transform = `rotate(${restingRotation(wonTier)}deg)`;
      if (startStep === 'done') showDone(wonTier);
      else showClaim(wonTier);
    }

    if (typeof root.showModal === 'function') root.showModal();
    else root.setAttribute('open', '');
    lockScroll(true);
    focusStep(startStep);
  };

  // Don't stack on top of the cart drawer, search, quick-add, etc.
  const pageIsBusy = () =>
    Array.from(document.querySelectorAll('dialog[open]')).some((dialog) => dialog !== root) ||
    document.documentElement.hasAttribute('scroll-lock');

  const openWhenClear = (startStep) => {
    loadCss().then((ok) => {
      if (!ok) return;
      const attempt = () => {
        if (document.visibilityState === 'visible' && !pageIsBusy()) {
          open(startStep);
          return true;
        }
        return false;
      };
      if (attempt()) return;
      const timer = window.setInterval(() => {
        if (attempt()) window.clearInterval(timer);
      }, 800);
    });
  };

  // Resolve once the page has been visible (tab in front) for `ms` in total.
  const afterVisibleFor = (ms, callback) => {
    let visibleMs = 0;
    let last = performance.now();
    const timer = window.setInterval(() => {
      const now = performance.now();
      // Cap each tick so time spent in a background tab never counts.
      if (document.visibilityState === 'visible') visibleMs += Math.min(now - last, 400);
      last = now;
      if (visibleMs >= ms) {
        window.clearInterval(timer);
        callback();
      }
    }, 250);
  };

  const start = () => {
    const state = readState();

    // Returning from Shopify's challenge page after a successful signup.
    if (state.status === 'pending' && params.get('customer_posted') === 'true' && tiers[state.won]?.code) {
      wonTier = state.won;
      writeState({ status: 'claimed', won: wonTier });
      openWhenClear('done');
      return;
    }

    if (!isEligible()) return;

    // Someone who already spun gets their same prize back (no re-rolling).
    const resumeTier = Number.isInteger(state.won) && tiers[state.won]?.code ? state.won : null;
    const startStep = resumeTier !== null ? 'claim' : 'spin';
    if (resumeTier !== null) wonTier = resumeTier;

    spinBtn.addEventListener('click', spin, { once: true });
    loadCss();

    const delayMs = isTestLink ? 800 : Math.max(0, Number(config.delaySeconds) || 0) * 1000;
    afterVisibleFor(delayMs, () => openWhenClear(startStep));
  };

  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
})();
